import Refresh from '@betfan/icons/duotone/Arrows/Refresh.svg?react';
import TicketStar from '@betfan/icons/duotone/Money/TickerStar.svg?react';
import { DSProgressBar } from 'designSystem/DSProgressBar';
import { RewardCondition } from 'features/RewardCard/components/RewardCondition';
import { RewardProgressSectionProps } from 'features/RewardCard/RewardCard.types';
import { getLocalProgressData, getSlipBetTypesLabel, removeRewardProgressLocalData, rewardKeys } from 'features/RewardCard/utils/utils';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const RewardProgressSection = memo(
	({ bonusId = 0, unlockConditions, separator = true, unlockedSum, unlockedBets, isBonusActive = false }: RewardProgressSectionProps) => {
		const { t } = useTranslation();
		const { allowedSlipBetTypes, minimumOddsProduct, totalSumToUnlock, totalBetsToUnlock } = unlockConditions;
		const hasAmountBar = totalSumToUnlock > 0;
		const hasBetsBar = totalBetsToUnlock != null;
		const amountKey = rewardKeys.amountUnlocked(bonusId);
		const betsKey = rewardKeys.betsUnlocked(bonusId);
		const localProgress = getLocalProgressData(amountKey);
		const localBets = getLocalProgressData(betsKey);
		const [showAmountBar, setShowAmountBar] = useState(!localProgress.isComplete);
		const [showBetsBar, setShowBetsBar] = useState(!localBets.isComplete);
		const slipBetTypes = getSlipBetTypesLabel(t, allowedSlipBetTypes);

		const getRewardConditionsDescription = () => {
			const betTypes = slipBetTypes ? `${t('promoConditions_coupon')} ${slipBetTypes}` : '';

			if (!minimumOddsProduct) {
				return betTypes;
			}

			const odds = `${t('minimumCouponOdds')} ${minimumOddsProduct.toFixed(2)}`;
			return betTypes ? `${odds}, ${betTypes}` : odds;
		};

		useEffect(() => {
			if (isBonusActive) {
				removeRewardProgressLocalData(bonusId);
			}
		}, [isBonusActive]);

		useEffect(() => {
			if ((hasAmountBar && unlockedSum < localProgress.amount) || (hasBetsBar && unlockedBets < localBets.amount)) {
				removeRewardProgressLocalData(bonusId, false);
				setShowAmountBar(true);
				setShowBetsBar(true);
			}
		}, [unlockedSum, unlockedBets, bonusId, hasAmountBar, hasBetsBar, localProgress.amount, localBets.amount]);

		if (!showAmountBar || !showBetsBar) {
			return null;
		}

		return (
			<>
				{separator && <div className="h-1 bg-gray-100 dark:bg-gray-900 rounded-4 my-1" />}

				<RewardCondition
					title={t('wagerDepositFunds')}
					description={getRewardConditionsDescription()}
					isComplete={unlockedSum === totalSumToUnlock}
					icon={<Refresh className="w-3 h-3 text-success-500" />}
				/>
				<DSProgressBar
					className="ml-5"
					progressType="amount"
					current={unlockedSum}
					total={totalSumToUnlock}
					label={t('rewardCardProgressAmount', { unlockedSum, totalSumToUnlock })}
					onComplete={() => {
						isBonusActive && localStorage.setItem(amountKey, JSON.stringify({ amount: unlockedSum, isComplete: true }));
					}}
				/>
				{hasBetsBar && (
					<>
						<RewardCondition
							title={t('placeCouponsForDeposit')}
							description={getRewardConditionsDescription()}
							isComplete={unlockedBets === totalBetsToUnlock}
							icon={<TicketStar className="w-3 h-3 text-success-500" />}
						/>
						<DSProgressBar
							className="ml-5"
							progressType="bets"
							current={unlockedBets}
							total={totalBetsToUnlock}
							label={t('rewardCardProgressBets', { unlockedBets, totalBetsToUnlock })}
							onComplete={() => {
								isBonusActive && localStorage.setItem(betsKey, JSON.stringify({ amount: unlockedBets, isComplete: true }));
							}}
						/>
					</>
				)}
			</>
		);
	},
);

RewardProgressSection.displayName = 'RewardProgressSection';
